// videoIntrinsicSize.js
'use strict';

/**
 * Whether an intrinsic video size can be used. Anything missing, non-finite or
 * not positive means the browser has no decoded frame size yet.
 * @param {object} params
 * @param {number} [params.videoWidth] - HTML video intrinsic width.
 * @param {number} [params.videoHeight] - HTML video intrinsic height.
 * @returns {boolean} True when both values are usable.
 * @example
 * getIsValidVideoSize({ videoWidth: 1920, videoHeight: 1080 }); // => true
 */
const getIsValidVideoSize = ({ videoWidth, videoHeight }) =>
  Number.isFinite(videoWidth) &&
  Number.isFinite(videoHeight) &&
  videoWidth > 0 &&
  videoHeight > 0;

/**
 * Report a video element's intrinsic size now and whenever it changes. Invalid
 * sizes are never reported, so whatever the caller stored last is the
 * last-known-good size; reset it when the source itself changes.
 * @param {object} params
 * @param {HTMLVideoElement} [params.video] - The rendered video element.
 * @param {Function} params.onSize - Called with { videoWidth, videoHeight }.
 * @returns {Function} Unsubscribe function.
 * @example
 * const unobserve = observeVideoIntrinsicSize({
 *   video: container.querySelector('video'),
 *   onSize: setVideoSize,
 * });
 */
const observeVideoIntrinsicSize = ({ video, onSize }) => {
  if (!video || typeof onSize !== 'function') return () => {};

  let lastSize = null;
  const readSize = () => {
    const { videoWidth, videoHeight } = video;
    if (!getIsValidVideoSize({ videoWidth, videoHeight })) return;
    if (
      lastSize &&
      lastSize.videoWidth === videoWidth &&
      lastSize.videoHeight === videoHeight
    ) {
      return;
    }
    lastSize = { videoWidth, videoHeight };
    onSize(lastSize);
  };

  readSize();
  // loadedmetadata covers the first decoded frame, resize covers rotation,
  // camera switch and remote stream type changes.
  video.addEventListener('loadedmetadata', readSize);
  video.addEventListener('resize', readSize);
  return () => {
    video.removeEventListener('loadedmetadata', readSize);
    video.removeEventListener('resize', readSize);
  };
};

export { observeVideoIntrinsicSize };
